import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { firestore } from '../firebaseConfig';
import {
  collection, query, where, getDocs,
  deleteDoc, doc, orderBy,
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

  :root {
    --bg:          #0a1628;
    --bg-card:     #0f1e35;
    --bg-card-h:   #132440;
    --teal:        #00adb5;
    --teal-dim:    rgba(0,173,181,0.12);
    --teal-border: rgba(0,173,181,0.22);
    --accent:      #ff5722;
    --white:       #f0f4f8;
    --muted:       rgba(240,244,248,0.45);
    --transition:  0.22s cubic-bezier(0.4,0,0.2,1);
  }

  .ma-page {
    min-height: 100vh;
    background: linear-gradient(150deg, #0d2240 0%, #0a1628 40%, #080f1c 100%);
    font-family: 'DM Sans', sans-serif;
    padding: 40px 24px 80px;
  }

  .ma-inner {
    max-width: 900px;
    margin: 0 auto;
  }

  /* ── Topo ── */
  .ma-topbar {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 32px;
  }
  .ma-badge {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: var(--teal-dim);
    border: 1px solid var(--teal-border);
    border-radius: 20px;
    padding: 4px 12px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--teal);
    margin-bottom: 10px;
  }
  .ma-badge-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: var(--teal);
    animation: pulse-dot 1.8s ease-in-out infinite;
  }
  @keyframes pulse-dot {
    0%,100% { opacity:1; transform:scale(1); }
    50%      { opacity:.4; transform:scale(.7); }
  }
  .ma-title {
    font-size: 26px;
    font-weight: 600;
    color: var(--white);
    margin-bottom: 4px;
  }
  .ma-sub {
    font-size: 13px;
    color: var(--muted);
  }
  .ma-btn-new {
    display: flex;
    align-items: center;
    gap: 7px;
    background: var(--teal);
    border: none;
    border-radius: 8px;
    color: #fff;
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px;
    font-weight: 600;
    padding: 10px 20px;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
    transition: background var(--transition), box-shadow var(--transition), transform var(--transition);
  }
  .ma-btn-new:hover {
    background: #009aa2;
    box-shadow: 0 4px 16px rgba(0,173,181,0.35);
    transform: translateY(-1px);
  }

  /* ── Resumo ── */
  .ma-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
    margin-bottom: 28px;
  }
  .ma-stat {
    background: var(--bg-card);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 10px;
    padding: 16px 18px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .ma-stat__value {
    font-size: 26px;
    font-weight: 600;
    color: var(--white);
    line-height: 1;
  }
  .ma-stat__label {
    font-size: 11.5px;
    color: var(--muted);
    letter-spacing: 0.05em;
  }
  .ma-stat__dot {
    width: 8px; height: 8px;
    border-radius: 50%;
    margin-bottom: 6px;
  }

  /* ── Filtros ── */
  .ma-filters {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 20px;
  }
  .ma-filter-btn {
    background: transparent;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 20px;
    padding: 5px 14px;
    font-family: 'DM Sans', sans-serif;
    font-size: 12.5px;
    color: var(--muted);
    cursor: pointer;
    transition: all var(--transition);
    white-space: nowrap;
  }
  .ma-filter-btn:hover { border-color: rgba(255,255,255,0.25); color: var(--white); }
  .ma-filter-btn.active {
    background: var(--teal-dim);
    border-color: var(--teal-border);
    color: var(--teal);
  }
  .ma-search {
    flex: 1;
    min-width: 160px;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 8px;
    padding: 7px 12px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    color: var(--white);
    outline: none;
    transition: border-color var(--transition), background var(--transition);
  }
  .ma-search::placeholder { color: rgba(240,244,248,0.2); }
  .ma-search:focus { border-color: var(--teal-border); background: var(--teal-dim); }

  /* ── Lista ── */
  .ma-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .ma-item {
    background: var(--bg-card);
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 12px;
    padding: 18px 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    transition: background var(--transition), border-color var(--transition);
    animation: fadeUp .3s ease both;
  }
  .ma-item:hover {
    background: var(--bg-card-h);
    border-color: var(--teal-border);
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .ma-item__icon {
    width: 40px; height: 40px;
    border-radius: 10px;
    background: var(--teal-dim);
    border: 1px solid var(--teal-border);
    display: flex; align-items: center; justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
  }

  .ma-item__info { flex: 1; min-width: 0; }
  .ma-item__name {
    font-family: 'DM Mono', monospace;
    font-size: 14px;
    font-weight: 500;
    color: var(--white);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 4px;
  }
  .ma-item__meta {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
  .ma-item__setor {
    font-size: 11px;
    color: var(--teal);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    opacity: 0.8;
  }
  .ma-item__sep {
    width: 3px; height: 3px;
    border-radius: 50%;
    background: rgba(255,255,255,0.15);
    flex-shrink: 0;
  }
  .ma-item__date {
    font-size: 11.5px;
    color: var(--muted);
  }

  /* Status badge */
  .ma-status {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 11.5px;
    font-weight: 600;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .ma-status__dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }

  .ma-status--analise  { background: rgba(255,193,7,0.1);  border: 1px solid rgba(255,193,7,0.25);  color: #ffc107; }
  .ma-status--analise .ma-status__dot  { background: #ffc107; animation: pulse-dot 1.8s infinite; }

  .ma-status--disponivel { background: rgba(0,173,181,0.1); border: 1px solid var(--teal-border); color: var(--teal); }
  .ma-status--disponivel .ma-status__dot { background: var(--teal); }

  .ma-status--vendido  { background: rgba(76,175,80,0.1);  border: 1px solid rgba(76,175,80,0.25); color: #66bb6a; }
  .ma-status--vendido .ma-status__dot  { background: #66bb6a; }

  .ma-status--reservado { background: rgba(33,150,243,0.1); border: 1px solid rgba(33,150,243,0.25); color: #64b5f6; }
  .ma-status--reservado .ma-status__dot { background: #64b5f6; }

  .ma-status--default  { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); color: var(--muted); }
  .ma-status--default .ma-status__dot  { background: var(--muted); }

  /* Botão remover */
  .ma-item__remove {
    background: transparent;
    border: 1px solid transparent;
    border-radius: 7px;
    color: rgba(240,244,248,0.25);
    width: 34px; height: 34px;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    font-size: 16px;
    transition: background var(--transition), border-color var(--transition), color var(--transition);
  }
  .ma-item__remove:hover {
    background: rgba(255,87,34,0.1);
    border-color: rgba(255,87,34,0.3);
    color: #ff7043;
  }

  /* ── Empty ── */
  .ma-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    padding: 80px 20px;
    text-align: center;
  }
  .ma-empty__icon { font-size: 42px; opacity: 0.3; }
  .ma-empty__title { font-size: 17px; font-weight: 600; color: var(--white); }
  .ma-empty__sub { font-size: 13px; color: var(--muted); max-width: 300px; line-height: 1.6; }
  .ma-empty__btn {
    background: var(--teal); border: none; border-radius: 8px;
    color: #fff; font-family: 'DM Sans', sans-serif;
    font-size: 14px; font-weight: 600; padding: 11px 24px;
    cursor: pointer; margin-top: 4px;
    transition: background var(--transition), box-shadow var(--transition);
  }
  .ma-empty__btn:hover { background: #009aa2; box-shadow: 0 4px 16px rgba(0,173,181,0.35); }

  /* ── Spinner ── */
  .ma-spinner-wrap {
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    gap: 16px; padding: 80px 20px;
  }
  .ma-spinner {
    width: 40px; height: 40px;
    border-radius: 50%;
    border: 3px solid var(--teal-dim);
    border-top-color: var(--teal);
    animation: spin 0.9s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── Modal de confirmação ── */
  .ma-modal-backdrop {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.65);
    backdrop-filter: blur(4px);
    z-index: 1000;
    display: flex; align-items: center; justify-content: center;
    padding: 20px;
    animation: fadeIn .2s ease;
  }
  @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
  .ma-modal {
    background: #0f1e35;
    border: 1px solid var(--teal-border);
    border-radius: 14px;
    padding: 32px 28px;
    width: 100%; max-width: 380px;
    box-shadow: 0 24px 64px rgba(0,0,0,0.6);
    animation: modalIn .22s cubic-bezier(.4,0,.2,1);
  }
  @keyframes modalIn {
    from { opacity:0; transform:translateY(12px) scale(.97); }
    to   { opacity:1; transform:translateY(0) scale(1); }
  }
  .ma-modal__icon { font-size: 32px; margin-bottom: 12px; }
  .ma-modal__title { font-size: 17px; font-weight: 600; color: var(--white); margin-bottom: 8px; }
  .ma-modal__sub { font-size: 13px; color: var(--muted); line-height: 1.6; margin-bottom: 6px; }
  .ma-modal__domain {
    font-family: 'DM Mono', monospace;
    font-size: 13px; color: var(--teal);
    background: var(--teal-dim);
    border: 1px solid var(--teal-border);
    border-radius: 5px; padding: 6px 12px;
    display: inline-block; margin: 10px 0 20px;
    word-break: break-all;
  }
  .ma-modal__actions { display: flex; gap: 10px; }
  .ma-modal__cancel {
    flex: 1; background: transparent;
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 8px; color: var(--muted);
    font-family: 'DM Sans', sans-serif;
    font-size: 14px; font-weight: 500;
    padding: 11px; cursor: pointer;
    transition: all var(--transition);
  }
  .ma-modal__cancel:hover { border-color: rgba(255,255,255,0.25); color: var(--white); }
  .ma-modal__confirm {
    flex: 1; background: rgba(255,87,34,0.15);
    border: 1px solid rgba(255,87,34,0.35);
    border-radius: 8px; color: #ff7043;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px; font-weight: 600;
    padding: 11px; cursor: pointer;
    transition: all var(--transition);
    display: flex; align-items: center; justify-content: center; gap: 7px;
  }
  .ma-modal__confirm:hover:not(:disabled) {
    background: rgba(255,87,34,0.25);
    border-color: rgba(255,87,34,0.55);
  }
  .ma-modal__confirm:disabled { opacity: .55; cursor: not-allowed; }

  /* Não logado */
  .ma-unauth {
    display: flex; flex-direction: column;
    align-items: center; text-align: center; gap: 14px;
    padding: 80px 20px;
  }
  .ma-unauth__icon { font-size: 42px; opacity: .35; }
  .ma-unauth__title { font-size: 18px; font-weight: 600; color: var(--white); }
  .ma-unauth__sub { font-size: 13px; color: var(--muted); line-height: 1.6; max-width: 300px; }
  .ma-unauth__btn {
    background: var(--teal); border: none; border-radius: 8px;
    color: #fff; font-family: 'DM Sans', sans-serif;
    font-size: 14px; font-weight: 600; padding: 12px 28px;
    cursor: pointer; margin-top: 4px;
    transition: background var(--transition), box-shadow var(--transition);
  }
  .ma-unauth__btn:hover { background: #009aa2; box-shadow: 0 4px 16px rgba(0,173,181,0.35); }

  @media (max-width: 540px) {
    .ma-item { flex-wrap: wrap; }
    .ma-item__remove { margin-left: auto; }
    .ma-status { font-size: 11px; }
  }
`;

// ── Helpers ───────────────────────────────────────────────────────────────────
const STATUS_MAP = {
  'em análise':  { cls: 'analise',    label: 'Em análise' },
  'publicado':   { cls: 'disponivel', label: 'Publicado'  },
  'disponível':  { cls: 'disponivel', label: 'Disponível' },
  'consultar':   { cls: 'disponivel', label: 'Disponível' },
  'vendido':     { cls: 'vendido',    label: 'Vendido'    },
  'reservado':   { cls: 'reservado',  label: 'Reservado'  },
};

const getStatus = (raw = '') => {
  const key = raw.toLowerCase().trim();
  return STATUS_MAP[key] || { cls: 'default', label: raw || 'Indefinido' };
};

const formatDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const FILTERS = ['Todos', 'Em análise', 'Publicado', 'Reservado', 'Vendido'];

// ── Componente ────────────────────────────────────────────────────────────────
export default function MeusAnuncios() {
  const navigate = useNavigate();
  const auth     = getAuth();

  const [user, setUser]         = useState(undefined);
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filter, setFilter]     = useState('Todos');
  const [toRemove, setToRemove] = useState(null);   // item a remover
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u ?? null));
    return () => unsub();
  }, []);

  const fetchItems = useCallback(async (email) => {
    setLoading(true);
    try {
      const q    = query(
        collection(firestore, 'dominios'),
        where('contato', '==', email),
        orderBy('data', 'desc'),
      );
      const snap = await getDocs(q);
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
      // Tenta sem orderBy (índice pode não existir ainda)
      try {
        const q2   = query(collection(firestore, 'dominios'), where('contato', '==', email));
        const snap2 = await getDocs(q2);
        const list  = snap2.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (b.data?.seconds || 0) - (a.data?.seconds || 0));
        setItems(list);
      } catch (err2) { console.error(err2); }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) fetchItems(user.email);
    else if (user === null) setLoading(false);
  }, [user, fetchItems]);

  // ── Remoção ──
  const confirmRemove = async () => {
    if (!toRemove) return;
    setRemoving(true);
    try {
      await deleteDoc(doc(firestore, 'dominios', toRemove.id));
      setItems((prev) => prev.filter((i) => i.id !== toRemove.id));
      setToRemove(null);
    } catch (err) {
      console.error(err);
    } finally {
      setRemoving(false);
    }
  };

  // ── Filtros ──
  const filtered = items.filter((item) => {
    const matchSearch = !search.trim() ||
      item.nome?.toLowerCase().includes(search.toLowerCase()) ||
      item.setor?.toLowerCase().includes(search.toLowerCase());

    const matchFilter = filter === 'Todos' ||
      getStatus(item.status).label.toLowerCase() === filter.toLowerCase() ||
      (filter === 'Publicado' && item.status?.toLowerCase() === 'publicado');

    return matchSearch && matchFilter;
  });

  // ── Stats ──
  const stats = {
    total:      items.length,
    analise:    items.filter((i) => i.status?.toLowerCase().includes('análise')).length,
    publicados: items.filter((i) => i.status?.toLowerCase() === 'publicado').length,
    vendidos:   items.filter((i) => i.status?.toLowerCase() === 'vendido').length,
  };

  // ── Loading auth ──
  if (user === undefined) {
    return (
      <>
        <style>{styles}</style>
        <div className="ma-page">
          <div className="ma-inner">
            <div className="ma-spinner-wrap">
              <div className="ma-spinner" />
              <span style={{ color: 'var(--muted)', fontSize: 13 }}>Verificando acesso...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Não logado ──
  if (!user) {
    return (
      <>
        <style>{styles}</style>
        <div className="ma-page">
          <div className="ma-inner">
            <div className="ma-unauth">
              <div className="ma-unauth__icon">🔒</div>
              <div className="ma-unauth__title">Acesso restrito</div>
              <p className="ma-unauth__sub">Faça login para visualizar seus anúncios.</p>
              <button className="ma-unauth__btn" onClick={() => navigate('/')}>Entrar</button>
            </div>
          </div>
        </div>
      </>
    );
  }

  const displayName = user.displayName || user.email.split('@')[0];

  return (
    <>
      <style>{styles}</style>

      {/* Modal de confirmação */}
      {toRemove && (
        <div className="ma-modal-backdrop" onClick={(e) => e.target === e.currentTarget && setToRemove(null)}>
          <div className="ma-modal">
            <div className="ma-modal__icon">🗑️</div>
            <div className="ma-modal__title">Remover anúncio?</div>
            <p className="ma-modal__sub">Esta ação é permanente. O domínio será excluído do catálogo.</p>
            <div className="ma-modal__domain">{toRemove.nome}</div>
            <div className="ma-modal__actions">
              <button className="ma-modal__cancel" onClick={() => setToRemove(null)} disabled={removing}>
                Cancelar
              </button>
              <button className="ma-modal__confirm" onClick={confirmRemove} disabled={removing}>
                {removing
                  ? <><div className="ma-spinner" style={{ width:14, height:14, borderWidth:2, borderTopColor:'#ff7043', borderColor:'rgba(255,112,67,.2)' }} /> Removendo...</>
                  : '🗑 Remover'
                }
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="ma-page">
        <div className="ma-inner">

          {/* Topo */}
          <div className="ma-topbar">
            <div>
              <div className="ma-badge"><span className="ma-badge-dot" />Meus anúncios</div>
              <div className="ma-title">Olá, {displayName} 👋</div>
              <div className="ma-sub">Gerencie os domínios que você anunciou na plataforma.</div>
            </div>
            <button className="ma-btn-new" onClick={() => navigate('/Anunciar')}>
              + Novo anúncio
            </button>
          </div>

          {/* Stats */}
          {!loading && items.length > 0 && (
            <div className="ma-stats">
              {[
                { label: 'Total anunciados', value: stats.total,      color: '#00adb5' },
                { label: 'Em análise',       value: stats.analise,    color: '#ffc107' },
                { label: 'Publicados',       value: stats.publicados, color: '#00adb5' },
                { label: 'Vendidos',         value: stats.vendidos,   color: '#66bb6a' },
              ].map((s) => (
                <div className="ma-stat" key={s.label}>
                  <div className="ma-stat__dot" style={{ background: s.color }} />
                  <div className="ma-stat__value">{s.value}</div>
                  <div className="ma-stat__label">{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Filtros + busca */}
          {!loading && items.length > 0 && (
            <div className="ma-filters">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  className={`ma-filter-btn${filter === f ? ' active' : ''}`}
                  onClick={() => setFilter(f)}
                >
                  {f}
                </button>
              ))}
              <input
                className="ma-search"
                type="text"
                placeholder="Buscar domínio ou setor..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          )}

          {/* Conteúdo */}
          {loading ? (
            <div className="ma-spinner-wrap">
              <div className="ma-spinner" />
              <span style={{ color: 'var(--muted)', fontSize: 13 }}>Carregando seus anúncios...</span>
            </div>
          ) : items.length === 0 ? (
            <div className="ma-empty">
              <div className="ma-empty__icon">📭</div>
              <div className="ma-empty__title">Nenhum anúncio ainda</div>
              <p className="ma-empty__sub">Você ainda não anunciou nenhum domínio. Comece agora e coloque seu ativo à venda.</p>
              <button className="ma-empty__btn" onClick={() => navigate('/Anunciar')}>
                + Anunciar domínio
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="ma-empty">
              <div className="ma-empty__icon">🔍</div>
              <div className="ma-empty__title">Nenhum resultado</div>
              <p className="ma-empty__sub">Tente ajustar os filtros ou o termo de busca.</p>
            </div>
          ) : (
            <div className="ma-list">
              {filtered.map((item, i) => {
                const st = getStatus(item.status);
                return (
                  <div
                    className="ma-item"
                    key={item.id}
                    style={{ animationDelay: `${i * 40}ms` }}
                  >
                    <div className="ma-item__icon">🌐</div>

                    <div className="ma-item__info">
                      <div className="ma-item__name">{item.nome}</div>
                      <div className="ma-item__meta">
                        {item.setor && (
                          <>
                            <span className="ma-item__setor">{item.setor}</span>
                            <span className="ma-item__sep" />
                          </>
                        )}
                        <span className="ma-item__date">{formatDate(item.data)}</span>
                        {item.valor && (
                          <>
                            <span className="ma-item__sep" />
                            <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                              R$ {Number(item.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className={`ma-status ma-status--${st.cls}`}>
                      <div className="ma-status__dot" />
                      {st.label}
                    </div>

                    <button
                      className="ma-item__remove"
                      title="Remover anúncio"
                      onClick={() => setToRemove(item)}
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          )}

        </div>
      </div>
    </>
  );
}
